"use client";

import { useState, useTransition } from "react";
import {
  clearSquadStatus,
  saveSquadStatus,
} from "@/app/actions/companion-people";
import type { CompanionPeopleSquad, CompanionStatusPin, SquadStatusType } from "@/lib/companion/people-loop";

const STATUSES: Array<{ value: SquadStatusType; label: string }> = [
  { value: "out_now", label: "Out now" },
  { value: "at_lunch", label: "At lunch" },
  { value: "pub", label: "At the pub" },
  { value: "heading_home", label: "Heading home" },
  { value: "watching_conditions", label: "Watching conditions" },
  { value: "rest_day", label: "Rest day" },
];

export function CompanionStatusPins({
  statusPins,
  squads,
  selectedDate,
  selectedSquadId,
  timezone,
  defaultPlace,
  unavailable,
  viewerUserId,
}: {
  statusPins: CompanionStatusPin[];
  squads: CompanionPeopleSquad[];
  selectedDate: string;
  selectedSquadId: string | null;
  timezone: string;
  defaultPlace: { slug: string | null; name: string } | null;
  unavailable: boolean;
  viewerUserId: string | null;
}) {
  const [pending, startTransition] = useTransition();
  const [message, setMessage] = useState<string | null>(null);

  function submit(formData: FormData) {
    if (!defaultPlace) return;
    startTransition(async () => {
      const result = await saveSquadStatus({
        squadId: String(formData.get("squad") ?? ""),
        statusType: String(formData.get("status") ?? "out_now"),
        targetLocalDate: selectedDate,
        timezone,
        placeSlug: defaultPlace.slug,
        placeName: defaultPlace.name,
        approximateLabel: String(formData.get("approximate-label") ?? "").trim() || null,
        meetupId: null,
      });
      setMessage(result.ok ? "Squad status updated." : result.error);
    });
  }

  function clear(status: CompanionStatusPin) {
    startTransition(async () => {
      const result = await clearSquadStatus({ squadId: status.squadId });
      setMessage(result.ok ? "Squad status cleared." : result.error);
    });
  }

  return (
    <section aria-labelledby="people-status-heading" className="rounded-md border border-border-light bg-white p-4 md:p-5">
      <p className="text-[11px] font-semibold uppercase tracking-[1.6px] text-teal">Approximate status</p>
      <h2 id="people-status-heading" className="mt-1 font-serif text-[26px] leading-tight text-navy">Where the day is at</h2>
      {unavailable ? (
        <p role="status" className="mt-3 rounded-sm border border-terracotta/35 bg-terracotta/10 px-3 py-2 text-[12px] text-navy">Approximate statuses are unavailable right now.</p>
      ) : statusPins.length > 0 ? (
        <ul className="mt-4 grid gap-2">
          {statusPins.map((status) => (
            <li key={status.id} className="flex min-h-[64px] items-center justify-between gap-3 rounded-sm border border-border-light bg-warm-bg px-3 py-2">
              <div>
                <p className="text-[13px] font-semibold text-navy">{status.displayName} · {statusLabel(status.statusType)}</p>
                <p className="mt-1 text-[12px] text-text-muted">{status.placeName}{status.approximateLabel ? ` · ${status.approximateLabel}` : ""}</p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                <span className="rounded-full border border-teal/25 bg-teal/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.8px] text-teal-dark">Approximate</span>
                {viewerUserId === status.userId ? (
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => clear(status)}
                    className="min-h-[44px] rounded-sm border border-terracotta/40 bg-white px-3 text-[11px] font-semibold text-terracotta hover:border-terracotta disabled:opacity-60"
                  >
                    Clear my status
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-[13px] leading-relaxed text-text-muted">No approximate statuses are active. Status never starts live location sharing.</p>
      )}

      {squads.length > 0 && defaultPlace ? (
        <form action={submit} className="mt-4 grid gap-2 border-t border-border-light pt-4 sm:grid-cols-[minmax(130px,1fr)_minmax(150px,1fr)_minmax(150px,1fr)_auto] sm:items-end">
          <label className="text-[11px] font-semibold text-navy">Squad<select name="squad" defaultValue={selectedSquadId ?? squads[0]?.id} className="mt-1 min-h-[44px] w-full rounded-sm border border-border bg-white px-3 text-[13px] text-navy">{squads.map((squad) => <option key={squad.id} value={squad.id}>{squad.name}</option>)}</select></label>
          <label className="text-[11px] font-semibold text-navy">Status<select name="status" defaultValue="out_now" className="mt-1 min-h-[44px] w-full rounded-sm border border-border bg-white px-3 text-[13px] text-navy">{STATUSES.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}</select></label>
          <label className="text-[11px] font-semibold text-navy">Rough area<input name="approximate-label" maxLength={80} placeholder="North end" className="mt-1 min-h-[44px] w-full rounded-sm border border-border bg-white px-3 text-[13px] text-navy" /></label>
          <button type="submit" disabled={pending} className="min-h-[44px] rounded-sm bg-navy px-4 text-[13px] font-semibold text-white hover:bg-navy-hover disabled:opacity-60">{pending ? "Updating..." : "Set status"}</button>
        </form>
      ) : null}
      <p aria-live="polite" className="mt-2 min-h-5 text-[12px] text-text-muted">{message}</p>
    </section>
  );
}

function statusLabel(value: SquadStatusType): string {
  return STATUSES.find((status) => status.value === value)?.label ?? value.replaceAll("_", " ");
}
